import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import i18next from '$shared/i18n/i18n';

export function createCurrentDateString(): string {
  const date = new Date();
  return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
}

export function getLocalizedTime(UTCUnixTime: string | number): string {
  return i18next.t('translation:shared.intlDateTime', {
    val: new Date(UTCUnixTime),
    formatParams: {
      val: {
        hour: 'numeric',
        minute: 'numeric',
        hourCycle: 'h23',
      },
    },
  });
}

export function getLocalizedMonthAndDay(UTCUnixTime: string | number): string {
  return i18next.t('translation:shared.intlDateTime', {
    val: new Date(UTCUnixTime),
    formatParams: {
      val: {
        month: 'numeric',
        day: 'numeric',
      },
    },
  });
}

export function getLocalizedTimeWithSeconds(
  dateInput: string | number,
  localTime = false,
): string {
  const date = new Date(dateInput);

  const formatParams: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hourCycle: 'h23',
  };

  if (!localTime) {
    // Only force UTC if not local
    formatParams.timeZone = 'UTC';
  }

  return i18next.t('translation:shared.intlDateTime', {
    val: date,
    formatParams: {
      val: formatParams,
    },
  });
}

export function getLocalizedDate(UTCUnixTime: string | number): string {
  return i18next.t('translation:shared.intlDateTime', {
    val: new Date(UTCUnixTime),
    formatParams: {
      val: {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      },
    },
  });
}

export function getLocalizedDateTime(UTCUnixTime: string | number): string {
  return i18next.t('translation:shared.intlDateTime', {
    val: new Date(UTCUnixTime),
    formatParams: {
      val: {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
      },
    },
  });
}

export function getFormattedLocalizedDate(UTCUnixTime: string | number): string {
  return format(UTCUnixTime, 'dd.MM.yyyy, HH:mm');
}

export function getFormattedLocalizedDateWithMonthName(
  UTCUnixTime: string | number,
): string {
  return format(UTCUnixTime, 'dd MMMM yyyy, HH:mm');
}

export function getFormattedLocalizedDateTimeWithSeconds(
  UTCUnixTime: string | number,
): string {
  return format(UTCUnixTime, 'dd.MM.yyyy, HH:mm:ss');
}

export function formatDateRange(
  fromDate: string,
  toDate: string,
  includeTime = false,
): string {
  try {
    const parsedFrom = parseISO(fromDate);
    const parsedTo = parseISO(toDate);
    const pattern = includeTime ? 'dd MMM yyyy, HH:mm' : 'dd MMM yyyy';
    return `${format(parsedFrom, pattern)} - ${format(parsedTo, pattern)}`;
  } catch {
    return '-';
  }
}

export function getFormattedLocalizedDateNoTime(
  dateInput: string | number,
  localTime = false,
): string {
  if (!dateInput) return '';

  const date =
    // Handle unix timestamp (number in seconds or milliseconds)
    typeof dateInput === 'number'
      ? // If timestamp is in seconds (Unix timestamp), convert to milliseconds
        new Date(dateInput < 10000000000 ? dateInput * 1000 : dateInput)
      : parseISO(dateInput);

  // Use local time
  if (localTime) {
    return format(date, 'dd.MM.yyyy');
  }

  // Handle ISO string by parsing the date without timezone conversion
  return formatInTimeZone(date, 'UTC', 'dd.MM.yyyy');
}
